import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Flame, Plus } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";

const STAGES = [
  { key: "new", label: "New" },
  { key: "engaged", label: "Engaged" },
  { key: "warm", label: "Warm" },
  { key: "hot", label: "Hot" },
  { key: "payment_sent", label: "Payment Sent" },
  { key: "paid", label: "Paid" },
];

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ name: "", handle: "", source: "instagram" });

  const load = async () => {
    const { data } = await api.get("/leads");
    setLeads(data);
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/leads", form);
      toast.success("Lead added");
      setForm({ name: "", handle: "", source: "instagram" });
      setShow(false);
      load();
    } catch {
      toast.error("Failed");
    }
  };

  const moveStage = async (id, stage) => {
    await api.patch(`/leads/${id}`, { stage });
    load();
  };

  const byStage = (s) => leads.filter((l) => l.stage === s);

  return (
    <div className="min-h-screen bg-[#FAFAFA] p-6 lg:p-10" data-testid="leads-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold text-ink">Lead Intelligence</h1>
          <p className="text-meta text-sm mt-1">Deterministic scoring · AI-prioritized pipeline</p>
        </div>
        <button onClick={() => setShow(true)} className="rounded-full ashra-gradient-bg px-4 py-2 text-sm font-semibold text-white inline-flex items-center gap-1 shadow-sm hover:shadow-md transition focus-ring" data-testid="add-lead-btn">
          <Plus className="h-4 w-4" /> Add lead
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {STAGES.map((s) => (
          <div key={s.key} className="surface p-3 min-h-[500px]">
            <div className="flex items-center justify-between mb-3 px-1">
              <h3 className="text-sm font-bold text-ink">{s.label}</h3>
              <span className="text-xs text-meta font-semibold">{byStage(s.key).length}</span>
            </div>
            <div className="space-y-2">
              {byStage(s.key).map((l) => (
                <motion.div
                  layout
                  key={l.id}
                  className="rounded-2xl border border-line bg-zinc-50 hover:bg-white hover:shadow-sm transition p-3"
                  data-testid={`lead-${l.id}`}
                >
                  <div className="flex items-center gap-2">
                    <div className="h-7 w-7 rounded-full ashra-gradient-bg grid place-items-center text-[10px] font-bold text-white shrink-0">{l.name?.[0]}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-ink truncate">{l.name}</p>
                      <p className="text-[10px] text-meta truncate">{l.handle}</p>
                    </div>
                    {l.purchase_probability >= 0.8 && <Flame className="h-3 w-3 text-warning shrink-0" />}
                  </div>
                  <div className="mt-2 flex items-center justify-between text-[10px]">
                    <span className="text-meta font-medium">Score {l.score}</span>
                    <span className="text-success font-bold">${Math.round(l.revenue_potential)}</span>
                  </div>
                  <div className="mt-1.5 h-1 rounded-full bg-zinc-200 overflow-hidden">
                    <div className="h-full rounded-full ashra-gradient-bg" style={{ width: `${l.score*100}%` }} />
                  </div>
                  <select
                    value={l.stage}
                    onChange={(e) => moveStage(l.id, e.target.value)}
                    className="mt-2 w-full rounded-lg bg-white border border-line text-xs px-2 py-1 text-ink-soft focus:outline-none focus:ring-2 focus:ring-violet-500"
                    data-testid={`stage-select-${l.id}`}
                  >
                    {STAGES.map(x => <option key={x.key} value={x.key}>{x.label}</option>)}
                    <option value="lost">Lost</option>
                  </select>
                </motion.div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {show && (
        <div className="fixed inset-0 z-50 bg-black/55 backdrop-blur-sm grid place-items-center p-4" onClick={() => setShow(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={create} className="w-full max-w-md surface p-6" data-testid="add-lead-modal">
            <h3 className="font-display text-xl font-bold text-ink mb-4">New lead</h3>
            <div className="space-y-2">
              <input required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="Name" className="w-full rounded-xl bg-white border border-line px-3 py-2 text-sm text-ink placeholder:text-meta-soft focus:outline-none focus:ring-2 focus:ring-violet-500" />
              <input required value={form.handle} onChange={(e) => setForm({...form, handle: e.target.value})} placeholder="@handle or phone" className="w-full rounded-xl bg-white border border-line px-3 py-2 text-sm text-ink placeholder:text-meta-soft focus:outline-none focus:ring-2 focus:ring-violet-500" />
              <select value={form.source} onChange={(e) => setForm({...form, source: e.target.value})} className="w-full rounded-xl bg-white border border-line px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-violet-500">
                <option value="instagram">Instagram</option>
                <option value="whatsapp">WhatsApp</option>
                <option value="landing_page">Landing page</option>
              </select>
            </div>
            <button type="submit" className="mt-4 w-full rounded-full ashra-gradient-bg py-2.5 text-sm font-semibold text-white shadow-sm hover:shadow-md transition">Add lead</button>
          </form>
        </div>
      )}
    </div>
  );
}
